<template>
  <v-navigation-drawer
    v-if="$auth.isAuthenticated"
    ref="drawerRef"
    :model-value="drawerVisible"
    class="pb-3 navigation-drawer-height"
    rail-width="60"
    :rail="display.lgAndUp && isDrawerCollapsible"
    :width="270"
    :location="display.mdAndDown ? 'right' : undefined"
    :expand-on-hover="true"
    @update:rail="handleDrawerRail"
    @mouseenter="handleMouseEvent(true)"
    @mouseleave="handleMouseEvent(false)"
  >
    <!-- Company Logo (shown at the top of navigation when the app header is disabled) -->
    <template
      v-if="!isAppHeaderActive"
      v-slot:prepend
    >
      <CompanyLogo
        :is-drawer-open="isDrawerExpanded"
        class="company-logo"
        @track-click="trackClick('Logo')"
      />
    </template>

    <!-- User Section -->
    <!-- User Menu Section -->
    <FeatureFlag
      :feature-name="featureNames.USAGE_COUNTER"
      @feature-change="onUsageCounterFeatureChange"
    >
      <v-divider />
      <v-list-item class="px-0">
        <MainNavigation
          :is-drawer-open="isDrawerExpanded"
          :user-picture="$auth.user.picture"
          :user-name="$auth.user.given_name + ' ' + $auth.user.family_name"
          :company-name="company?.name"
          @track-click="trackClick($event)"
          @logout="logout"
        />
      </v-list-item>
      <v-divider />
    </FeatureFlag>
    <ViewSwitch
      v-if="!inOnboarding && hasChildCompany"
      :show-label="showLabel"
      @switch-view="switchView()"
    />
    <div class="navigation-container">
      <!-- Primary Navigation -->
      <PrimaryNavigationItems
        :nav-items="navItems"
        :show-regeneration="showRegeneration"
        @navigation-click="handleItemClick($event)"
      />
      <v-divider />
      <!-- Secondary Navigation -->
      <SecondaryNavigationItems
        :nav-items="filteredNavItemsBottimmo"
        :is-drawer-open="isDrawerExpanded"
        :unread-news-count="unreadNewsCount"
        :show-regeneration="showRegeneration"
        @navigation-click="handleItemClick($event)"
      />
    </div>

    <!-- User Area -->
    <template v-slot:append>
      <FeatureFlag :feature-name="featureNames.USAGE_COUNTER">
        <template v-slot="props">
          <v-list-item
            v-if="isDrawerExpanded && company"
            class="usage-counter"
            @mounted="isUsageCounterActive = true"
          >
            <UsageCounter
              :product-type="productType"
              v-bind="props"
            />
          </v-list-item>
        </template>
        <template #fallback>
          <!-- User Area -->
          <UpgradeButton
            v-if="isEntryPlan && !isPlatformSmg && !route.path.startsWith('/master')"
            style="width: 100%"
            variant="elevated"
            class="my-4"
            :label="$t('navigation.labels.upgrade')"
            :show-label="showLabel"
            @track-click="$tracking.event('Sidebar', 'Clicked', 'Upgrade')"
          />

          <!-- TODO: check unsupported prop: offset-y -->
          <FooterNavigation
            :is-drawer-open="isDrawerExpanded"
            :product-type="productType"
            :company-name="company?.name"
            :user-picture="$auth.user.picture"
            :company-state="company?.state"
            @track-click="trackClick($event)"
            @mounted="isUsageCounterActive = false"
            @logout="logout"
          />
        </template>
      </FeatureFlag>
    </template>
  </v-navigation-drawer>
</template>
<script lang="ts" setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import FeatureFlag from '@/components/FeatureFlag.vue'
import UsageCounter from '@/components/UsageCounter.vue'
import { useBranding } from '@/composables/useBranding'
import useFeature from '@/composables/useFeature'
import { useNavigationDrawer } from '@/composables/useNavigationDrawer'
import featureNames from '@/lib/featureNames.js'
import featureMixin from '@/mixins/feature.js'
import CompanyLogo from '@/modules/navigation/CompanyLogo.vue'
import FooterNavigation from '@/modules/navigation/FooterNavigation.vue'
import MainNavigation from '@/modules/navigation/MainNavigation.vue'
import { NavItem } from '@/modules/navigation/models/Navitem'
import PrimaryNavigationItems from '@/modules/navigation/PrimaryNavigationItems.vue'
import SecondaryNavigationItems from '@/modules/navigation/SecondaryNavigationItems.vue'
import UpgradeButton from '@/modules/navigation/UpgradeButton.vue'
import ViewSwitch from '@/modules/navigation/ViewSwitch.vue'
import HAS_CHILD_COMPANY from '@/queries/HasChildCompany.gql'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

const route = useRoute()

const props = defineProps<{
  display: Record<string, boolean>
  navItems: NavItem[]
  filteredNavItemsBottimmo: NavItem[]
  unreadNewsCount: number
  inOnboarding: boolean
  company?: {
    id: string
    productType?: string
    name: string
    state: {
      stringValues?: string[]
    }
  }
  isDrawerCollapsible: boolean
  isEntryPlan: boolean | undefined
}>()

const emit = defineEmits<{
  (e: 'navigation-click', value: string): void
  (e: 'track-click', value: string): void
  (e: 'switch-view'): void
  (e: 'logout'): void
}>()

const { appContext } = getCurrentInstance()!
const $tracking = appContext.config.globalProperties.$tracking
const $auth = appContext.config.globalProperties.$auth
const { isDrawerOpen: drawerVisible } = useNavigationDrawer()
const isDrawerExpanded = ref(!props.isDrawerCollapsible)
const isUsageCounterActive = ref(false)
const showLabel = ref<boolean>()
const drawerRef = ref(null)
const { isPlatformSmg } = useBranding()
const { isFeatureActive } = useFeature()
const isAppHeaderActive = computed(() => isFeatureActive(featureNames.APP_HEADER))
const companyId = computed(() => props.company?.id)
const variables = computed(() => ({
  companyId: companyId.value || null,
}))

const { result } = useQuery(PENDING_CHANGES_FOR_REGENERATION, variables, {
  enabled: computed(() => !!companyId.value),
})

const pendingChangesForRegeneration = computed(() => {
  return result.value?.pendingChangesForRegeneration || {}
})

const showRegeneration = computed(() => {
  return (
    featureMixin.methods.isFeatureActive(featureNames.REGENERATION) &&
    Object.keys(pendingChangesForRegeneration.value).length > 0
  )
})

const productType = computed(() => props.company?.productType)

const { result: hasChildCompanyData } = useQuery(HAS_CHILD_COMPANY, null, {
  fetchPolicy: 'cache-first',
})
const hasChildCompany = computed(() => hasChildCompanyData.value?.hasChildCompany)

const handleItemClick = (label: string) => {
  emit('navigation-click', label)
}

const trackClick = (label: string) => {
  emit('track-click', label)
}

const switchView = () => {
  emit('switch-view')
}

const logout = () => {
  emit('logout')
}

const onUsageCounterFeatureChange = (event: { enabled: boolean }) => {
  isUsageCounterActive.value = event.enabled
}

const handleMouseEvent = (show: boolean) => {
  if (props.isDrawerCollapsible) {
    showLabel.value = show
  }
}

const handleDrawerRail = (value: boolean) => {
  if (props.isDrawerCollapsible) {
    isDrawerExpanded.value = !value
  }
}

watch(
  () => [props.isDrawerCollapsible, props.display.lgAndUp],
  ([collapsible, lg]) => {
    const rail = lg && collapsible
    isDrawerExpanded.value = !rail
    showLabel.value = !rail
  },
  {
    immediate: true,
  }
)
</script>

<style scoped>
:deep(.v-navigation-drawer--mini-variant .v-list-item__spacer) {
  display: none;
}

:deep(.v-icon--size-default) {
  font-size: 20px;
}

:deep(.upgrade-button-label) {
  font-size: 16px;
}

:deep(.v-list-item__spacer) {
  display: none;
}

:deep(.list-item--variant-text) {
  color: unset;
}

.navigation-drawer-height ::v-deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.navigation-drawer-height ::v-deep(.v-list:last-child),
.navigation-drawer-height ::v-deep(.v-navigation-drawer__append) {
  margin-top: auto;
  margin-bottom: 0;
}

.usage-counter {
  padding: 0 !important;
}

.company-logo {
  min-height: 68px;
}

::v-deep(.v-app-bar__nav-icon .v-icon) {
  font-size: 30px !important;
  width: 30px !important;
  height: 30px !important;
}
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 16px;
}

.navigation-container {
  display: flex;
  flex-direction: column;
  height: v-bind("isUsageCounterActive ? 'calc(100% - 50px)' : '100%'");
}

.navigation-drawer-height ::v-deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
}

:deep(.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line) {
  padding-inline: 16px !important;
}
</style>
